/**
 * State machine for the reader page text: drag selection, phrase groups,
 * translation, highlights, and audio position.
 *
 * This is the declarative replacement for the webapp's use of the imperative
 * `spanTranslator` module (since deleted; the browser extension, where
 * Readlang doesn't own the page DOM, uses `rangeTranslator`). All state lives
 * in signals; the
 * Preact reader text components render directly from them, so untranslating a
 * phrase is just deleting its group - the pristine token model re-renders.
 */
import {
  computed,
  effect,
  signal,
  type ReadonlySignal,
  type Signal,
} from "@preact/signals";

import * as envelope from "envelope";
import * as haptics from "haptics";
import * as objectIdUtils from "objectIdUtils";
import * as readerTokens from "readerText/tokens";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";

export type ReaderTextGroup = {
  id: number;
  from: number;
  to: number;
  status: "translating" | "translated";
  monolingual: boolean;
  translation?: string;
  alternativeTranslations: string[];
  transliteration?: string;
  /** True while the strike-through deletion animation plays. */
  struck: boolean;
  /** Whitespace-consolidated source text, as sent for translation. */
  queryText: string;
};

/** A lookup saved to the server, by the group it was saved from */
type SentPhrase = { groupId: number; id: string; contextID: string };

/**
 * Why a group is untranslated: the user deselecting it, a new group covering
 * it (whose save undoes this lookup), or anything else, which keeps the lookup
 */
type UntranslateReason = "deselected" | "absorbed" | "dismissed";

/**
 * A lookup still saving, and what its save does: records it while its group
 * shows or is absorbed by a phrase yet to save (which then undoes it), or
 * undoes it. One kept or dismissed is removed, so its save records nothing.
 */
type SavingLookup = readerTokens.PositionRange & {
  fate: "record" | "absorbed" | "undo";
};

export type ReaderTextPointerTarget = {
  position?: number;
  groupId?: number;
};

type SetupOptions = {
  sourceLanguage: string;
  scrollMode?: boolean;
  stores: { user: any; remoteUserWords: any };
  bookModel?: any;
  translator: (
    data: object,
    callback: (translationResponse: any) => void,
    errorCallback: () => void,
    transliterationCallback?: (transliteration: string) => void
  ) => void | Promise<void>;
  translateDictionaryLookup?: (
    userWord: any,
    range: readerTokens.PositionRange
  ) => void;
  userWordCreator: (wordDefinition: object) => any;
  deleteOnUntranslate?: boolean;
  contextBeforePage?: string;
  beforeTranslate?: () => void;
  /**
   * Reading-guide hook: given the text of a group about to be translated,
   * returns true when it matches a glossary entry and has been handled (shown
   * in a popover), in which case the normal translate flow is deferred to the
   * popover: it must eventually call exactly one of `dismiss` (undo the
   * selection) or `translate` (continue the normal translate flow).
   * `neighborWord` returns the text of the word `offset` positions from the
   * group's first word (undefined at text boundaries), letting the lookup
   * match one clicked word of a multi-word name.
   */
  glossaryLookup?: (
    text: string,
    actions: {
      range: readerTokens.PositionRange;
      neighborWord: (offset: number) => string | undefined;
      dismiss: () => void;
      translate: () => void;
    }
  ) => boolean;
  speak?: (language: string, text: string) => void;
  chooseFirstLanguage?: () => Promise<void>;
  updateLayout?: (retainPosition?: number) => void;
  /**
   * Reader-only hooks which let a drag that dwells on the last visible line
   * pull the next line of text onto the page, so that phrases spanning a page
   * break can be selected in one drag. Hosts without viewport pagination
   * omit this.
   */
  lineNudger?: {
    /**
     * True if dwelling on the word/group covering `range` should pull in the
     * next line, i.e. it is on the last visible line and its paragraph
     * continues below the page break (translations can't cross paragraph
     * breaks, so shifting would otherwise be pointless).
     */
    shouldShiftForRange: (range: readerTokens.PositionRange) => boolean;
    /**
     * Returns false when the page could not actually be shifted, so that the
     * nudge stops retrying for the rest of the drag.
     */
    shiftPageDownOneLine: () => boolean;
  };
};

/** How long a drag must dwell on the last visible line before pulling in the next line */
export const LINE_NUDGE_DELAY_MS = 1000;

type DragFrom = {
  start: number;
  end: number;
  groupId?: number;
  /** Whether the drag selects single spans rather than whole words (see `dragStart`) */
  byCharacter?: boolean;
};

/**
 * Punctuation characters. Make sure it's kept in sync with punctuation in
 * `wordRegExp` in spannify.js
 */
const lonePunctuation = '«»"”“.,?!¡¿'.split("");

let nextInstanceKey = 1;

const rangeBounds = (...positions: number[]) => ({
  start: Math.min(...positions),
  end: Math.max(...positions),
});

const isWithin = ({ from, to }: readerTokens.PositionRange, position: number) =>
  position >= from && position <= to;

const positionsIn = ({ from, to }: readerTokens.PositionRange) =>
  Array.from({ length: to - from + 1 }, (_, index) => from + index);

/** Positions whose membership differs between two position-keyed collections. */
const changedPositions = (
  before: { keys: () => Iterable<number>; has: (key: number) => boolean },
  after: { keys: () => Iterable<number>; has: (key: number) => boolean }
): number[] => {
  // Collections are replaced, never mutated, so an identical reference means
  // this source didn't change - skip scanning it (yourPhrases can hold tens
  // of thousands of entries, and this runs on every drag-move tick).
  if (before === after) {
    return [];
  }
  const changed: number[] = [];
  for (const position of before.keys()) {
    if (!after.has(position)) {
      changed.push(position);
    }
  }
  for (const position of after.keys()) {
    if (!before.has(position)) {
      changed.push(position);
    }
  }
  return changed;
};

/** The changed positions between two single-position values. */
const changedSingle = (
  previous: number | undefined,
  next: number | undefined
): number[] =>
  previous === next
    ? []
    : [previous, next].filter((value) => value !== undefined);

/**
 * A family of per-key signals derived from one source signal, updated by
 * diffing consecutive source values - the reactive analogue of Solid's
 * createSelector. Reads stay declarative (`forKey` returns a signal, seeded
 * on first use), but no key subscribes to the source: a single effect diffs
 * each new source value against the previous one and writes only the keys
 * reported as changed. An update therefore costs the diff plus the changed
 * keys, no matter how many keys exist - which is what makes per-word state
 * affordable on a book-length text (forum #5245), where a signal-per-key
 * *computed* would subscribe every word to the source and recompute all of
 * them on any change.
 */
const keyedSignals = <Source, Value>(
  source: ReadonlySignal<Source>,
  valueForKey: (current: Source, key: number) => Value,
  changedKeys: (before: Source, after: Source) => Iterable<number>
): { forKey: (key: number) => ReadonlySignal<Value>; dispose: () => void } => {
  const signalsByKey = new Map<number, Signal<Value>>();
  let previous = source.peek();
  const dispose = effect(() => {
    const next = source.value;
    for (const key of changedKeys(previous, next)) {
      const keyed = signalsByKey.get(key);
      if (keyed) {
        keyed.value = valueForKey(next, key);
      }
    }
    previous = next;
  });
  return {
    forKey: (key: number) => {
      let keyed = signalsByKey.get(key);
      if (!keyed) {
        keyed = signal(valueForKey(source.peek(), key));
        signalsByKey.set(key, keyed);
      }
      return keyed;
    },
    dispose,
  };
};

/** The gloss text shown for a translated group (main + alternatives). */
export const groupTranslationText = (group: ReaderTextGroup): string =>
  [group.translation, ...group.alternativeTranslations].join(" / ");

export default class ReaderTextStore {
  /**
   * Unique per instance. Views subscribe to a store's signals on mount, and a
   * fresh store is created for every page load, so components that take a
   * store prop must be keyed by this to remount (and re-subscribe) when the
   * page swaps stores under a preserved reader page.
   */
  readonly instanceKey = nextInstanceKey++;

  readonly tokens = signal<readerTokens.ParsedReaderText | undefined>(
    undefined
  );
  readonly groups = signal<ReadonlyMap<number, ReaderTextGroup>>(new Map());
  readonly selectingPositions = signal<ReadonlySet<number>>(new Set());
  readonly selectingGroupIds = signal<ReadonlySet<number>>(new Set());
  /** The word under the mouse, when longer than one span (see `hoverWord`) */
  readonly hoverPositions = signal<ReadonlySet<number>>(new Set());
  readonly audioPosition = signal<number | undefined>(undefined);
  readonly audioSyncPositions = signal<ReadonlySet<number>>(new Set());
  readonly searchFlashPosition = signal<number | undefined>(undefined);
  readonly yourPhrases = signal<ReadonlyMap<number, string[]>>(new Map());
  readonly enabled = signal(false);
  readonly clickMode = signal<string>("translate");
  /**
   * First position rendered on the page. Pagination advances this to drop the
   * top line while keeping a just-translated bottom-row word on screen
   * (replaces the old DOM-mutating `removeFirstLineAndUpdate`).
   */
  readonly renderFromPosition = signal(0);

  readonly groupByPosition = computed(() => {
    const byPosition = new Map<number, ReaderTextGroup>();
    for (const group of this.groups.value.values()) {
      for (let position = group.from; position <= group.to; position++) {
        byPosition.set(position, group);
      }
    }
    return byPosition;
  });

  private options?: SetupOptions;
  private groupCounter = 1;
  /**
   * Map of position to info about the word at that position which has been
   * selected for translation, either on its own or as part of a phrase.
   * Mirrors the old spanTranslator bookkeeping (kept non-reactive).
   */
  private selectedPositions = new Map<number, { groupId?: number }>();
  /**
   * Map of position to the lookup saved there: the word's id and the id of the
   * context the lookup added, which undoing it removes.
   */
  private sentPhrases = new Map<number, SentPhrase>();
  /** The lookups still saving, by group id */
  private savingLookups = new Map<number, SavingLookup>();
  private dragFrom: DragFrom | null = null;
  private dragTo: number | null = null;
  /** Whether the pointer has left the word a drag started on */
  private dragMoved = false;
  private clickHandlers = new Map<string, (position: number) => void>();
  private yourWordClickHandler:
    | ((position: number, element: HTMLElement, context: string) => void)
    | undefined;
  /**
   * The word being read aloud: several spans in languages written without
   * spaces (see `readerTokens.getWordRange`)
   */
  private readonly audioPositions = computed(() => {
    const position = this.audioPosition.value;
    const parsed = this.tokens.value;
    return new Set(
      position === undefined
        ? []
        : positionsIn(
            parsed
              ? readerTokens.getWordRange(parsed, position)
              : { from: position, to: position }
          )
    );
  });
  /** The shared state a word's class string depends on, bundled for diffing. */
  private readonly wordClassSource = computed(() => ({
    selecting: this.selectingPositions.value,
    hover: this.hoverPositions.value,
    yourPhrases: this.yourPhrases.value,
    audioPositions: this.audioPositions.value,
    audioSync: this.audioSyncPositions.value,
    searchFlash: this.searchFlashPosition.value,
  }));

  /**
   * Per-word class signals (see `wordClass`). Any write to one of the source
   * signals above flows through `keyedSignals`' diffing effect, so call sites
   * just assign state and only the affected words re-render.
   */
  private readonly wordClasses = keyedSignals(
    this.wordClassSource,
    (source, position) =>
      utils.cn({
        selecting: source.selecting.has(position),
        hovering: source.hover.has(position),
        yourWord: source.yourPhrases.has(position),
        audioPosition: source.audioPositions.has(position),
        audioSync: source.audioSync.has(position),
        searchHighlightFlash: source.searchFlash === position,
      }),
    (previous, next) => [
      ...changedPositions(previous.selecting, next.selecting),
      ...changedPositions(previous.hover, next.hover),
      ...changedPositions(previous.yourPhrases, next.yourPhrases),
      ...changedPositions(previous.audioSync, next.audioSync),
      ...changedPositions(previous.audioPositions, next.audioPositions),
      ...changedSingle(previous.searchFlash, next.searchFlash),
    ]
  );

  /**
   * Cache for `groupsForItemRange`, keyed `"start:end"`. Not a performance
   * cache: segments call `groupsForItemRange` on every render and must get
   * the same signal instance back, or their mount-time subscription would
   * point at a dead signal. Cleared per tokenization (item indexes are only
   * meaningful for the tokenization they were computed against).
   */
  private groupsByItemRangeSignals = new Map<
    string,
    ReadonlySignal<readonly ReaderTextGroup[]>
  >();
  private searchFlashTimer: ReturnType<typeof setTimeout> | undefined;
  private lineNudgeTimer: ReturnType<typeof setTimeout> | undefined;
  /** Set when a nudge failed to shift the page, to stop retrying until the next drag */
  private lineNudgeFailed = false;

  setup(options: SetupOptions) {
    this.options = options;
    this.lineNudgeFailed = false;
    this.resetPositionKeyedState();
    this.setEnabled(true);
  }

  /**
   * Groups, selections, and in-flight drag state are keyed by token
   * positions, which are only meaningful for the tokenization they were
   * created against. They must be reset whenever the tokens change (page
   * chunk re-fetched, book text edited or re-transcribed): a stale group
   * painted over re-parsed tokens renders one full-translation span per
   * block its range overlaps, showing the whole phrase translation on each
   * fragment.
   */
  private resetPositionKeyedState() {
    this.groups.value = new Map();
    this.selectedPositions = new Map();
    this.sentPhrases = new Map();
    this.savingLookups = new Map();
    this.dragFrom = null;
    this.dragTo = null;
    this.cancelLineNudge();
    this.clearSelecting();
  }

  close() {
    document.body.removeAttribute("data-spansEnabled");
    this.enabled.value = false;
    clearTimeout(this.searchFlashTimer);
    this.cancelLineNudge();
    this.wordClasses.dispose();
  }

  setTokens(parsed: readerTokens.ParsedReaderText | undefined) {
    this.tokens.value = parsed;
    this.renderFromPosition.value = 0;
    this.groupsByItemRangeSignals = new Map();
    // The per-word class signals are deliberately kept: word views bind to the
    // signal instance they saw on mount, and a re-tokenized text updates them
    // in place rather than remounting every word.
    this.audioSyncPositions.value = new Set();
    this.hoverPositions.value = new Set();
    this.yourPhrases.value = new Map();
    this.searchFlashPosition.value = undefined;
    this.resetPositionKeyedState();
  }

  isScrollMode() {
    return Boolean(this.options?.scrollMode);
  }

  /** Text directions of the learning/first language pair, for bidi rendering. */
  getDirections():
    | { source: string | undefined; target: string | undefined }
    | undefined {
    const user = this.options?.stores.user;
    if (!user) {
      return undefined;
    }
    return {
      source:
        languageMap.map[user.state.value.currentLearningLanguage]?.direction,
      target: languageMap.map[user.state.value.firstLanguage]?.direction,
    };
  }

  setEnabled(enabled: boolean) {
    this.enabled.value = enabled;
    document.body.setAttribute(
      "data-spansEnabled",
      enabled ? "enabled" : "disabled"
    );
  }

  setClickMode(clickMode: string) {
    this.clickMode.value = clickMode;
    document.body.setAttribute("data-clickMode", clickMode);
  }

  setDeleteOnUntranslate(deleteOnUntranslate: boolean) {
    if (this.options) {
      this.options.deleteOnUntranslate = deleteOnUntranslate;
    }
  }

  /** Set click handler for a specific mode (e.g. "sync" or "goto" used by SyncAudioPlayer) */
  addClickHandler(mode: string, handler: (position: number) => void) {
    this.clickHandlers.set(mode, handler);
  }

  removeClickHandler(mode: string, handler: (position: number) => void) {
    if (this.clickHandlers.get(mode) === handler) {
      this.clickHandlers.delete(mode);
    }
  }

  setYourWordClickHandler(
    handler: (position: number, element: HTMLElement, context: string) => void
  ) {
    this.yourWordClickHandler = handler;
  }

  /**
   * Pure delegation, kept for call sites holding an optional store reference
   * (there is no optional-chaining assignment). Writing the signal directly
   * is equally correct: word-class refresh is driven by `wordClasses`' effect,
   * not by these methods.
   */
  setAudioPosition(position: number | undefined) {
    this.audioPosition.value = position;
  }

  /** Pure delegation; see `setAudioPosition`. */
  setAudioSyncPositions(positions: ReadonlySet<number>) {
    this.audioSyncPositions.value = positions;
  }

  flashSearchPosition(position: number) {
    clearTimeout(this.searchFlashTimer);
    this.searchFlashPosition.value = position;
    this.searchFlashTimer = setTimeout(() => {
      this.searchFlashPosition.value = undefined;
    }, 2000);
  }

  /**
   * Per-word class signal so only affected words re-render on state changes.
   * Derived via `keyedSignals` (see `wordClasses`) rather than as one
   * computed per word, which would subscribe every word in the text to the
   * shared selection/audio/highlight signals and recompute all of them on any
   * change - seconds of main-thread work on a long scroll-mode text
   * (forum #5245).
   */
  wordClass(position: number): ReadonlySignal<string> {
    return this.wordClasses.forKey(position);
  }

  /**
   * The groups overlapping one rendered segment of the text, keyed by the
   * segment's item-index range. Segments subscribe to this rather than to
   * `groups` so that translating a phrase only re-renders the segment
   * containing it; the previous array instance is returned when the segment's
   * groups are unchanged, which is what stops the signal from notifying every
   * other segment.
   */
  groupsForItemRange(
    startIndex: number,
    endIndex: number
  ): ReadonlySignal<readonly ReaderTextGroup[]> {
    const key = `${startIndex}:${endIndex}`;
    let rangeSignal = this.groupsByItemRangeSignals.get(key);
    if (!rangeSignal) {
      let previous: readonly ReaderTextGroup[] = [];
      rangeSignal = computed(() => {
        const parsed = this.tokens.value;
        const overlapping: ReaderTextGroup[] = [];
        if (parsed) {
          for (const group of this.groups.value.values()) {
            const range = readerTokens.getItemRange(parsed, group);
            if (
              range &&
              range.endIndex >= startIndex &&
              range.startIndex <= endIndex
            ) {
              overlapping.push(group);
            }
          }
          overlapping.sort((groupA, groupB) => groupA.from - groupB.from);
        }
        // Valid only because groups are immutable (updateGroup replaces the
        // object) - element-wise identity means "unchanged".
        const unchanged =
          overlapping.length === previous.length &&
          overlapping.every((group, index) => group === previous[index]);
        if (!unchanged) {
          previous = overlapping;
        }
        return previous;
      });
      this.groupsByItemRangeSignals.set(key, rangeSignal);
    }
    return rangeSignal;
  }

  getRangeForPosition(position: number): readerTokens.PositionRange {
    const group = this.groupByPosition.value.get(position);
    return group
      ? { from: group.from, to: group.to }
      : { from: position, to: position };
  }

  getContextSentence(range: readerTokens.PositionRange) {
    const parsed = this.tokens.value;
    if (!parsed) {
      throw Error("Reader text tokens are not loaded");
    }
    return readerTokens.getContextSentence(
      parsed,
      range,
      this.options?.contextBeforePage
    );
  }

  getSentenceFromPosition(
    position: number
  ): readerTokens.ContextSentenceNode[] {
    const parsed = this.tokens.value;
    if (!parsed) {
      return [];
    }
    return readerTokens.getSentenceFromPosition(
      parsed,
      this.getRangeForPosition(position),
      position,
      this.options?.contextBeforePage
    );
  }

  // ---------------------------------------------------------------------
  // Drag selection state machine (port of spanTranslator drag handlers)
  // ---------------------------------------------------------------------

  isDragging() {
    return this.dragFrom !== null;
  }

  /**
   * What pressing on a position selects: the position alone when it's in a
   * saved phrase (pressing that shows the saved phrase), or else the drag
   * unit there (see `dragUnit`).
   */
  private pressedRange(position: number): readerTokens.PositionRange {
    return this.yourWordClickHandler && this.yourPhrases.value.has(position)
      ? this.getRangeForPosition(position)
      : this.dragUnit(position);
  }

  /** The unit a drag selects: a word (see `wordRange`), or one span when dragging by character */
  private dragUnit(position: number): readerTokens.PositionRange {
    return this.dragFrom?.byCharacter
      ? this.getRangeForPosition(position)
      : this.wordRange(position);
  }

  /**
   * The translated group at a position, or else its whole word: several
   * spans in languages written without spaces (see
   * `readerTokens.getWordRange`), clipped where it runs into a translated
   * group, which a selection never partly overlaps, or, `savedOnly`, where it
   * runs out of saved phrases.
   */
  private wordRange(
    position: number,
    { savedOnly = false } = {}
  ): readerTokens.PositionRange {
    const parsed = this.tokens.value;
    const groupByPosition = this.groupByPosition.value;
    if (!parsed || groupByPosition.has(position)) {
      return this.getRangeForPosition(position);
    }
    const word = readerTokens.getWordRange(parsed, position);
    const joins = (other: number) =>
      !groupByPosition.has(other) &&
      (!savedOnly || this.yourPhrases.value.has(other));
    let from = position;
    let to = position;
    while (from > word.from && joins(from - 1)) {
      from--;
    }
    while (to < word.to && joins(to + 1)) {
      to++;
    }
    return { from, to };
  }

  /**
   * What a drag from `dragFrom` to `position` selects once the pointer has
   * left the pressed word: whole words, from the pressed word to the word or
   * group under the pointer, or, back within the pressed word, exactly the
   * spans dragged over, so a learner can pick out part of what
   * Intl.Segmenter joins (遍野 in 哀鸿遍野, or 我 in 我是).
   */
  private dragRange(dragFrom: DragFrom, position: number) {
    const pressed = this.dragUnit(dragFrom.start);
    if (isWithin(pressed, position)) {
      return rangeBounds(dragFrom.start, dragFrom.end, position);
    }
    const target = this.dragUnit(position);
    return rangeBounds(pressed.from, pressed.to, target.from, target.to);
  }

  /**
   * Highlights the word which pressing on `position` would select, or, in a
   * saved phrase (which pressing shows), the saved part of its word, when
   * it's longer than the one span which CSS `:hover` highlights.
   */
  hoverWord(position: number | undefined) {
    const range =
      position === undefined || this.isDragging()
        ? undefined
        : this.wordRange(position, {
            savedOnly:
              Boolean(this.yourWordClickHandler) &&
              this.yourPhrases.value.has(position),
          });
    const positions = new Set(
      range && range.from !== range.to ? positionsIn(range) : []
    );
    if (positions.size > 0 || this.hoverPositions.value.size > 0) {
      this.hoverPositions.value = positions;
    }
  }

  /**
   * Begin a selection drag on a word or group. Returns false when the
   * interaction should not be handled (spans disabled).
   *
   * A press selects the whole word (see `pressedRange`), and a drag extends
   * it word by word (see `dragRange`), or, `byCharacter` (Shift held), span
   * by span: Intl.Segmenter sometimes puts a word boundary where the learner
   * wouldn't (很好|吃, where they may want 好吃).
   */
  dragStart(
    target: ReaderTextPointerTarget,
    { byCharacter = false } = {}
  ): boolean {
    if (!this.enabled.value) {
      return false;
    }

    const clickHandler = this.clickHandlers.get(this.clickMode.value);
    if (clickHandler && target.position !== undefined) {
      clickHandler(target.position);
      return true;
    }

    if (target.position !== undefined) {
      this.dragFrom = {
        start: target.position,
        end: target.position,
        byCharacter,
      };
      this.selectingPositions.value = new Set(
        positionsIn(this.pressedRange(target.position))
      );
    } else if (target.groupId !== undefined) {
      const group = this.groups.value.get(target.groupId);
      if (!group) {
        return false;
      }
      this.dragFrom = {
        start: group.from,
        end: group.to,
        groupId: group.id,
        byCharacter,
      };
      this.selectingGroupIds.value = new Set([group.id]);
    } else {
      return false;
    }
    this.dragTo = this.dragFrom.end;
    this.dragMoved = false;
    this.hoverWord(undefined);

    this.cancelLineNudge();
    this.lineNudgeFailed = false;
    this.scheduleLineNudge(target);
    return true;
  }

  dragMove(target: ReaderTextPointerTarget | null) {
    if (this.dragFrom === null) {
      return;
    }
    this.scheduleLineNudge(target);
    if (
      !this.dragMoved &&
      target?.position !== undefined &&
      isWithin(this.dragUnit(this.dragFrom.start), target.position)
    ) {
      return;
    }
    this.dragMoved = true;
    if (
      !target ||
      (target.position === undefined && target.groupId === undefined)
    ) {
      this.clearSelecting();
      this.dragTo = null;
      return;
    }

    const parsed = this.tokens.value;
    if (!parsed) {
      return;
    }

    if (target.position !== undefined) {
      this.dragTo = target.position;
    } else {
      const group = this.groups.value.get(target.groupId as number);
      if (!group) {
        return;
      }
      this.dragTo = group.from;
    }
    const { start, end } = this.dragRange(this.dragFrom, this.dragTo);

    const referenceItem = readerTokens.getWordItem(parsed, this.dragFrom.start);
    const newSelecting = new Set<number>();

    for (let position = start; position <= end; position++) {
      const item = readerTokens.getWordItem(parsed, position);
      const coveredByGroup = this.groupByPosition.value.has(position);

      // A word is reachable from the drag origin iff it (1) shares the same
      // block root and (2) isn't separated from the origin by a <br> - a soft
      // paragraph boundary, matching cross-block behavior.
      if (
        item &&
        !coveredByGroup &&
        referenceItem &&
        item.blockIndex === referenceItem.blockIndex &&
        item.brIndex === referenceItem.brIndex
      ) {
        newSelecting.add(position);
      } else if (!coveredByGroup && !this.selectedPositions.has(position)) {
        this.dragTo = null;
      }
    }

    this.selectingPositions.value = newSelecting;
  }

  dragEnd(target: ReaderTextPointerTarget | null, element?: HTMLElement) {
    if (this.dragFrom === null || this.dragTo === null || isNaN(this.dragTo)) {
      this.resetDrag();
      return;
    }

    const dragFrom = this.dragFrom;
    let dragTo = this.dragTo;
    if (target?.position !== undefined) {
      dragTo = dragTo || target.position;
    }

    const pressed = this.pressedRange(dragFrom.start);
    const targetGroup =
      target?.groupId !== undefined
        ? this.groups.value.get(target.groupId)
        : undefined;
    const { start, end } = targetGroup
      ? this.dragRange(dragFrom, targetGroup.from)
      : this.dragMoved
        ? this.dragRange(dragFrom, dragTo)
        : rangeBounds(pressed.from, pressed.to);

    if (
      start === end &&
      this.yourWordClickHandler &&
      this.yourPhrases.value.has(start) &&
      !this.groupByPosition.value.has(start)
    ) {
      this.resetDrag();
      const context = this.getContextSentence({ from: start, to: end }).text;
      if (element) {
        this.yourWordClickHandler(start, element, context);
      }
      return;
    }

    const uniqueGroupIds = new Set<number | undefined>();
    for (let position = start; position <= end; position++) {
      uniqueGroupIds.add(this.selectedPositions.get(position)?.groupId);
    }
    const createGroupMode =
      (uniqueGroupIds.size === 1 && uniqueGroupIds.has(undefined)) ||
      uniqueGroupIds.size > 1;

    for (let position = start; position <= end; position++) {
      const selected = this.selectedPositions.get(position);
      if (selected) {
        const group =
          selected.groupId !== undefined
            ? this.groups.value.get(selected.groupId)
            : undefined;
        position = group?.to ?? position;
        if (group) {
          void this.untranslateGroup(
            group.id,
            createGroupMode ? "absorbed" : "deselected"
          );
        }
      }
    }

    // If createGroupMode is false here, it means that we've just unselected a
    // group. If it's true, we go ahead and create a new group.
    if (createGroupMode) {
      for (let position = start; position <= end; position++) {
        this.selectedPositions.set(position, {});
      }
      this.createGroup(start, end);
      haptics.play("light");
    }
    this.resetDrag();
  }

  cancelDrag() {
    this.resetDrag();
  }

  private resetDrag() {
    this.dragFrom = null;
    this.dragTo = null;
    this.cancelLineNudge();
    this.clearSelecting();
  }

  private cancelLineNudge() {
    if (this.lineNudgeTimer !== undefined) {
      clearTimeout(this.lineNudgeTimer);
      this.lineNudgeTimer = undefined;
    }
  }

  /**
   * Arms the line-nudge dwell timer while the drag hovers a word/group that
   * should pull the next line onto the page (port of spanTranslator's
   * scheduleLineNudge).
   */
  private scheduleLineNudge(target: ReaderTextPointerTarget | null) {
    const lineNudger = this.options?.lineNudger;
    if (!lineNudger || this.lineNudgeFailed) {
      return;
    }
    const range = target ? this.rangeForTarget(target) : undefined;
    if (!range || !lineNudger.shouldShiftForRange(range)) {
      this.cancelLineNudge();
      return;
    }
    if (this.lineNudgeTimer === undefined) {
      this.lineNudgeTimer = setTimeout(() => {
        this.lineNudgeTimer = undefined;
        // The drag may have ended, or a relayout may have moved the word off
        // the last line, since the timer was armed.
        if (this.dragFrom !== null && lineNudger.shouldShiftForRange(range)) {
          this.lineNudgeFailed = !lineNudger.shiftPageDownOneLine();
        }
      }, LINE_NUDGE_DELAY_MS);
    }
  }

  private rangeForTarget(
    target: ReaderTextPointerTarget
  ): readerTokens.PositionRange | undefined {
    if (target.position !== undefined) {
      return this.getRangeForPosition(target.position);
    }
    if (target.groupId !== undefined) {
      const group = this.groups.value.get(target.groupId);
      return group ? { from: group.from, to: group.to } : undefined;
    }
    return undefined;
  }

  private clearSelecting() {
    this.selectingPositions.value = new Set();
    this.selectingGroupIds.value = new Set();
  }

  // ---------------------------------------------------------------------
  // Group creation / merging (port of spanTranslator createGroup)
  // ---------------------------------------------------------------------

  private canGroup(from: number, to: number): boolean {
    const parsed = this.tokens.value;
    return parsed ? readerTokens.canGroupRange(parsed, from, to) : false;
  }

  private createGroup(position: number, toPosition?: number) {
    const parsed = this.tokens.value;
    if (!parsed || !this.options) {
      return;
    }

    let from = position;
    let to = toPosition ?? position;
    const id = this.groupCounter;

    const clearPending = () => {
      for (let innerPosition = from; innerPosition <= to; innerPosition++) {
        this.selectedPositions.delete(innerPosition);
      }
      // No phrase will undo the lookups absorbed here
      this.settleLookups({ from, to }, false);
    };

    if (!this.canGroup(from, to)) {
      clearPending();
      return;
    }

    for (let innerPosition = from; innerPosition <= to; innerPosition++) {
      const innerEntry = this.selectedPositions.get(innerPosition);
      if (innerEntry) {
        innerEntry.groupId = id;
      }
    }

    const groupsToRemove = new Set<number>();

    if (this.options.stores.user.state.value.readerMergePhrases === "off") {
      // skip merge
    } else {
      // Merge adjacent phrases. A neighbor is absorbed when it is already
      // selected/translated, shares the block root, and no sentence break
      // separates it from the selection.
      const sameBlock = (positionA: number, positionB: number) =>
        readerTokens.inSameBlock(parsed, positionA, positionB);
      const textBreakAfter = (innerPosition: number) =>
        readerTokens.hasTextBreakBetween(
          parsed,
          innerPosition,
          innerPosition + 1
        );

      const canAbsorb = (
        neighborPosition: number,
        direction: "left" | "right"
      ) => {
        const neighborEntry = this.selectedPositions.get(neighborPosition);
        if (!neighborEntry) {
          return false;
        }
        const neighborGroup =
          neighborEntry.groupId !== undefined
            ? this.groups.value.get(neighborEntry.groupId)
            : undefined;
        if (neighborEntry.groupId !== undefined && !neighborGroup) {
          return true;
        }
        const anchor = neighborGroup
          ? direction === "left"
            ? neighborGroup.to
            : neighborGroup.from
          : neighborPosition;
        // Break probes mirror the old sibling-walk semantics: leftward we test
        // the text after the absorbed neighbor; rightward we test after the
        // current selection for groups, but after the neighbor for plain
        // words (a long-standing quirk preserved for parity).
        const breaks =
          direction === "left"
            ? textBreakAfter(anchor)
            : neighborGroup
              ? textBreakAfter(anchor - 1)
              : textBreakAfter(neighborPosition);
        return sameBlock(anchor, position) && !breaks;
      };

      while (canAbsorb(from - 1, "left")) {
        from--;
        const absorbed = this.selectedPositions.get(from);
        if (absorbed?.groupId !== undefined) {
          groupsToRemove.add(absorbed.groupId);
        }
        this.selectedPositions.set(from, { groupId: id });
      }
      while (canAbsorb(to + 1, "right")) {
        to++;
        const absorbed = this.selectedPositions.get(to);
        if (absorbed?.groupId !== undefined) {
          groupsToRemove.add(absorbed.groupId);
        }
        this.selectedPositions.set(to, { groupId: id });
      }
    }

    // Remove absorbed groups, preserving the extended selection bookkeeping.
    const oldSelectedPositions = new Map(this.selectedPositions);
    for (const groupId of groupsToRemove) {
      if (this.groups.value.get(groupId)) {
        void this.untranslateGroup(groupId, "absorbed");
      }
    }
    this.selectedPositions = oldSelectedPositions;

    if (!this.canGroup(from, to)) {
      clearPending();
      return;
    }

    this.groupCounter++;
    const queryText = readerTokens
      .getRangeText(parsed, from, to)
      .replace(/\s+/g, " ")
      .trim();
    const group: ReaderTextGroup = {
      id,
      from,
      to,
      status: "translating",
      monolingual: false,
      alternativeTranslations: [],
      struck: false,
      queryText,
    };
    this.updateGroup(group);

    void this.translateGroup(group);
  }

  private updateGroup(group: ReaderTextGroup) {
    const newGroups = new Map(this.groups.value);
    newGroups.set(group.id, group);
    this.groups.value = newGroups;
  }

  private removeGroup(id: number) {
    if (!this.groups.value.has(id)) {
      return;
    }
    const newGroups = new Map(this.groups.value);
    newGroups.delete(id);
    this.groups.value = newGroups;
  }

  // ---------------------------------------------------------------------
  // Translate / untranslate (port of spanTranslator translateGroup et al)
  // ---------------------------------------------------------------------

  private monolingualMode() {
    return this.options?.stores.user.state.value.readerTranslations === "off";
  }

  private untranslateAllExcept(keepGroupId: number) {
    for (const group of [...this.groups.value.values()]) {
      if (group.id !== keepGroupId) {
        void this.untranslateGroup(group.id, "dismissed");
      }
    }
  }

  async untranslateGroup(id: number, reason: UntranslateReason = "deselected") {
    const group = this.groups.value.get(id);
    if (!group || !this.options) {
      return;
    }

    for (let position = group.from; position <= group.to; position++) {
      this.selectedPositions.delete(position);
    }

    const saving = this.savingLookups.get(id);
    const saved = this.sentPhrases.get(group.from)?.groupId === id;
    const deleting =
      Boolean(this.options.deleteOnUntranslate) && reason === "deselected";
    if (reason === "absorbed") {
      // Left, with any lookups it absorbed, for the phrase absorbing it to undo
      if (saving) {
        this.savingLookups.set(id, { ...saving, fate: "absorbed" });
      }
    } else {
      // Deleting undoes this lookup and those it absorbed, anything else keeps
      // them
      this.settleLookups(group, deleting);
      if (saving && deleting) {
        this.savingLookups.set(id, { ...saving, fate: "undo" });
      } else {
        this.savingLookups.delete(id);
      }
    }

    if (deleting && saved) {
      this.updateGroup({ ...group, struck: true });
      await utils.sleep(800);
    }

    this.removeGroup(id);
    this.options.updateLayout?.();
  }

  /**
   * Undoes the lookups saved within a range and those absorbed there still
   * saving, as a phrase covering them is saved or deleted, removing the
   * contexts they added (along with the whole word if that was its only
   * context). Otherwise forgets them, so they're kept.
   */
  private settleLookups(range: readerTokens.PositionRange, undo: boolean) {
    const settled = new Set<SentPhrase>();
    for (let position = range.from; position <= range.to; position++) {
      const sentPhrase = this.sentPhrases.get(position);
      if (sentPhrase) {
        settled.add(sentPhrase);
      }
    }
    for (const [position, sentPhrase] of this.sentPhrases) {
      if (settled.has(sentPhrase)) {
        this.sentPhrases.delete(position);
      }
    }
    for (const { id, contextID } of undo ? settled : []) {
      void this.options?.stores.remoteUserWords
        .getWord(id)
        ?.removeContext(contextID);
    }

    for (const [groupId, saving] of this.savingLookups) {
      if (
        saving.fate === "absorbed" &&
        saving.from <= range.to &&
        range.from <= saving.to
      ) {
        if (undo) {
          this.savingLookups.set(groupId, { ...saving, fate: "undo" });
        } else {
          this.savingLookups.delete(groupId);
        }
      }
    }
  }

  updateTranslations(word: string, translation: string) {
    for (const group of this.groups.value.values()) {
      if (group.queryText === word && group.status === "translated") {
        this.updateGroup({
          ...group,
          translation,
          alternativeTranslations: [],
        });
      }
    }
  }

  /**
   * Untranslates the lookups of words deleted elsewhere (e.g. the sidebar's
   * Words tab), except those already being struck out by their deselection
   */
  untranslateDeletedWords(wordIDs: string[]) {
    const deletedIDs = new Set(wordIDs);
    for (const [position, sentPhrase] of [...this.sentPhrases]) {
      if (deletedIDs.has(sentPhrase.id)) {
        this.sentPhrases.delete(position);
        const group = this.groupByPosition.value.get(position);
        if (group && !group.struck) {
          void this.untranslateGroup(group.id, "dismissed");
        }
      }
    }
  }

  private async translateGroup(group: ReaderTextGroup) {
    const options = this.options;
    const parsed = this.tokens.value;
    if (!options || !parsed) {
      return;
    }

    const rawText = readerTokens.getRangeText(parsed, group.from, group.to);
    const context = this.getContextSentence({
      from: group.from,
      to: group.to,
    });

    const data: any = {
      source: options.sourceLanguage,
      target: options.stores.user.state.value.firstLanguage,
      q: group.queryText,
      position: group.from === group.to ? String(group.from) : undefined,
      bookID: options.bookModel ? options.bookModel.id : undefined,
      context: context.text,
    };

    if (lonePunctuation.includes(data.q)) {
      // The user has clicked on a lone punctuation character, which we
      // shouldn't try to translate.
      void this.untranslateGroup(group.id, "dismissed");
      return;
    }

    if (options.glossaryLookup) {
      // The popover decides the selection's fate, so guard against it calling
      // both actions (or one of them twice)
      let settled = false;
      const handled = options.glossaryLookup(data.q, {
        range: { from: group.from, to: group.to },
        neighborWord: (offset) => {
          const neighborPosition = group.from + offset;
          return (
            readerTokens.getRangeText(
              parsed,
              neighborPosition,
              neighborPosition
            ) || undefined
          );
        },
        dismiss: () => {
          if (!settled) {
            settled = true;
            void this.untranslateGroup(group.id, "dismissed");
          }
        },
        translate: () => {
          if (!settled) {
            settled = true;
            void this.performGroupTranslation(group, rawText, context, data);
          }
        },
      });
      if (handled) {
        return;
      }
    }

    await this.performGroupTranslation(group, rawText, context, data);
  }

  /**
   * The main translate flow for a grouped selection: fetches the translation,
   * renders the gloss and saves the user word. Split from `translateGroup` so
   * the reading-guide glossary popover can run it as its "Translate" action.
   */
  private async performGroupTranslation(
    group: ReaderTextGroup,
    rawText: string,
    context: readerTokens.ContextSentence,
    data: any
  ) {
    const options = this.options;
    if (!options) {
      return;
    }

    options.beforeTranslate?.();
    options.speak?.(options.sourceLanguage, rawText);

    if (
      options.sourceLanguage === options.stores.user.state.value.firstLanguage
    ) {
      if (options.chooseFirstLanguage) {
        await options.chooseFirstLanguage();
      }

      data.target = options.stores.user.state.value.firstLanguage;

      // Only proceed if the user has now chosen an appropriate first language.
      if (
        options.sourceLanguage === options.stores.user.state.value.firstLanguage
      ) {
        alert(
          "Can't translate to the same language. Please ensure that you have different " +
            "languages set for your first language and the one you are reading. " +
            "Note: Even when using Readlang in monolingual mode, you still " +
            "need these languages to be different for Readlang to generate the flashcards you will practice with " +
            "later."
        );
        return;
      }
    }

    if (this.monolingualMode()) {
      // Monolingual mode shows one highlighted word/phrase at a time: dismiss
      // any other translations, and mark this group highlighted immediately
      // (its original text stays visible - no gloss is rendered).
      this.untranslateAllExcept(group.id);
      const current = this.groups.value.get(group.id);
      if (current) {
        this.updateGroup({
          ...current,
          status: "translated",
          monolingual: true,
        });
      }
    } else {
      // Clear any leftover monolingual highlights from a previous mode.
      for (const otherGroup of [...this.groups.value.values()]) {
        if (otherGroup.monolingual && otherGroup.id !== group.id) {
          void this.untranslateGroup(otherGroup.id, "dismissed");
        }
      }
    }

    const respondToTranslate = (translationResponse: any) => {
      const currentGroup = this.groups.value.get(group.id);
      if (!currentGroup || currentGroup.from !== group.from) {
        // The group was dismissed before the response arrived.
        return;
      }
      const translation = translationResponse.translations[0];
      const alternativeTranslations = translationResponse.translations.slice(1);

      if (!this.monolingualMode()) {
        if (currentGroup.status === "translating") {
          this.updateGroup({
            ...currentGroup,
            status: "translated",
            translation,
            alternativeTranslations,
          });
        } else {
          // don't translate if the user clicked another word
          return;
        }
      }

      const newWordDefinition: any = {
        word: group.queryText,
        language: options.sourceLanguage,
        translation: translation,
        contexts: [
          {
            _id: objectIdUtils.makeObjectId(),
            text: context.text,
          },
        ],
      };

      if (options.bookModel?.id) {
        const book = options.bookModel.book.value;
        newWordDefinition.contexts[0].bookID = options.bookModel.id;
        newWordDefinition.contexts[0].source = "book:" + options.bookModel.id;

        if (book.audioURL || book.youTubeID) {
          if (book.youTubeID) {
            newWordDefinition.contexts[0].youTubeID = book.youTubeID;
          } else {
            newWordDefinition.contexts[0].audioURL = book.audioURL;
          }
          newWordDefinition.contexts[0].audioStart = Math.max(
            0,
            envelope.getTime(book.audioMap, context.start) - 1
          );
          newWordDefinition.contexts[0].audioEnd =
            envelope.getTime(book.audioMap, context.end + 1) + 1;
        }
      }

      const newWord = options.userWordCreator(newWordDefinition);
      this.savingLookups.set(group.id, {
        from: group.from,
        to: group.to,
        fate: "record",
      });

      options.stores.remoteUserWords.addWord(newWord, (addedWord) => {
        options.translateDictionaryLookup?.(addedWord, {
          from: group.from,
          to: group.to,
        });

        const sentPhrase: SentPhrase = {
          groupId: group.id,
          id: addedWord.state.value._id,
          contextID: newWordDefinition.contexts[0]._id,
        };
        const saving = this.savingLookups.get(group.id);
        this.savingLookups.delete(group.id);
        if (saving?.fate === "undo") {
          void options.stores.remoteUserWords
            .getWord(sentPhrase.id)
            ?.removeContext(sentPhrase.contextID);
        } else if (saving) {
          // Saving a phrase undoes the lookups it absorbed
          this.settleLookups(group, true);
          for (let position = group.from; position <= group.to; position++) {
            this.sentPhrases.set(position, sentPhrase);
          }
        }

        options.stores.remoteUserWords.addUserWord(addedWord, {
          at: 0,
          merge: true,
        });
      });
    };

    void options.translator(
      data,
      respondToTranslate,
      () => {
        // Something went wrong, so un-translate
        void this.untranslateGroup(group.id, "dismissed");
      },
      (transliteration) => {
        // Add transliteration to the UI.
        // This must be called **after** the translation callback.
        const currentGroup = this.groups.value.get(group.id);
        if (!currentGroup || currentGroup.status !== "translated") {
          console.error("group isn't translated yet");
          return;
        }

        this.updateGroup({ ...currentGroup, transliteration });

        // Relayout retaining the group: the transliteration adds an extra line
        // above the translation, increasing widget height, which can push a
        // bottom-row selection onto the next page if we relayout without
        // retaining.
        setTimeout(() => options.updateLayout?.(group.from), 100);
      }
    );
  }
}
